
// Mercator normalized coords
function mercatorNormalized(lat: number, lon: number) {
    const sinLat = Math.sin((lat * Math.PI) / 180);
    const x = (lon + 180) / 360;
    const y = 0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI);
    return { x, y };
}

/**
 * Compute zoom such that `dest` is exactly centered and `other` is still visible
 * inside the container (after padding). Returns integer zoom (clamped).
 */
export function computeZoomKeepingDestCentered(
    destLat: number,
    destLon: number,
    otherLat: number,
    otherLon: number,
    containerWidthPx: number,
    containerHeightPx: number,
    padding = { left: 20, right: 20, top: 20, bottom: 80 }, // adjust bottom for overlays
    minZoom = 5,
    maxZoom = 16,
    safetyFactor = 0.9 // reduce available pixels slightly so markers aren't at the edge
): number {
    const availW = Math.max(1, containerWidthPx - (padding.left + padding.right));
    const availH = Math.max(
        1,
        containerHeightPx - (padding.top + padding.bottom)
    );
    // when dest is centered, other point must be inside half the width/height
    const halfW = (availW * safetyFactor) / 2;
    const halfH = (availH * safetyFactor) / 2;

    const pDest = mercatorNormalized(destLat, destLon);
    const pOther = mercatorNormalized(otherLat, otherLon);

    // normalized delta (handle wrap around)
    let dx = Math.abs(pOther.x - pDest.x);
    if (dx > 0.5) {
        dx = 1 - dx;
    }
    const dy = Math.abs(pOther.y - pDest.y);

    const worldTile = 256; // pixels at zoom 0
    // if same point or extremely close, return max zoom:
    if (dx === 0 && dy === 0) {
        return maxZoom;
    }

    // pixel distance at zoom Z = normDelta * worldTile * 2^Z
    // require: normDelta * worldTile * 2^Z <= halfW  =>  2^Z <= halfW / (normDelta * worldTile)
    // => Z <= log2( halfW / (normDelta * worldTile) )
    const zoomX = dx === 0 ? 1000 : Math.log2(halfW / (dx * worldTile));
    const zoomY = dy === 0 ? 1000 : Math.log2(halfH / (dy * worldTile));

    let targetZoom = Math.floor(Math.min(zoomX, zoomY));
    if (!isFinite(targetZoom)) { targetZoom = maxZoom };
    targetZoom = Math.max(minZoom, Math.min(maxZoom, targetZoom));
    return targetZoom;
}



// WEBPACK FOOTER //
// ./src/utils/helperFunctions.ts